import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createBuilding } from "../../../api/building";
import { getDevicesNotAssociatedToBuilding } from "../../../api/device";
import { DeviceWithoutLegacyPassword } from "../../../generated/types";
import { getDeviceDisplayName } from "../../../utils/breedingDevices";
import { Button } from "../../common/Button";
import { ContextSummary } from "../../common/ContextSummary";
import { FormSelect } from "../../common/FormLayout";
import { Modal, ModalActions } from "../../common/Modal";
import { sectionToIcon } from "../../type/default";
import { BUILDING_EXCLUDED_TYPES } from "../setup/AssociateDeviceForm";
import { SetupError } from "../setup/SetupLayout";
import {
  BuildingFields,
  EMPTY_BUILDING_FORM,
  toCreateBuilding,
  useBuildingForm,
} from "./BuildingFields";

type CreateBuildingModalProps = {
  breedingId: number;
  breedingName: string;
  companyId: number;
  companyName: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

/**
 * Création d'un bâtiment par un admin / PSC dans un élevage client, avec les mêmes champs
 * que côté éleveur. Le produit est facultatif : on peut rattacher tout de suite un produit
 * de l'entreprise encore sans bâtiment, ou le faire plus tard.
 */
export const CreateBuildingModal = ({
  breedingId,
  breedingName,
  companyId,
  companyName,
  isOpen,
  onClose,
  onSuccess,
}: CreateBuildingModalProps) => {
  const { t } = useTranslation();
  const buildingForm = useBuildingForm(EMPTY_BUILDING_FORM);
  const { reset } = buildingForm;
  const [devices, setDevices] = useState<DeviceWithoutLegacyPassword[]>([]);
  const [idDevice, setIdDevice] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    reset(EMPTY_BUILDING_FORM);
    setIdDevice("");
    setError(null);

    let cancelled = false;
    getDevicesNotAssociatedToBuilding(companyId).then((result) => {
      if (cancelled) return;
      setDevices(
        result.devices.filter(
          (device) => !BUILDING_EXCLUDED_TYPES.includes(device.type)
        )
      );
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, companyId, reset]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!buildingForm.validate()) return;

    setSubmitting(true);
    const result = await createBuilding({
      ...toCreateBuilding(buildingForm.form, breedingId),
      device: idDevice ? [Number(idDevice)] : [],
    });
    setSubmitting(false);

    if (!result.success) {
      setError(result.error || t("setup-error-building"));
      return;
    }
    onClose();
    onSuccess();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("building-create")}
      icon={sectionToIcon.section_building}
      maxWidth="md"
    >
      <form onSubmit={submit} className="space-y-4">
        <ContextSummary
          rows={[
            { label: t("building-create-for-breeding"), value: breedingName },
            { label: t("breeding-create-for-company"), value: companyName },
          ]}
        />
        <SetupError message={error} />

        <BuildingFields buildingForm={buildingForm} />

        {devices.length > 0 && (
          <FormSelect
            label={t("building-device-optional")}
            name="device"
            value={idDevice}
            onChange={(e) => setIdDevice(e.target.value)}
            options={devices.map((device) => ({
              value: String(device.id),
              label: `${getDeviceDisplayName(device)} (${device.type})`,
            }))}
          />
        )}

        <ModalActions>
          <Button type="button" variant="secondary" onClick={onClose}>
            {t("button-cancel")}
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? t("setup-saving") : t("building-create")}
          </Button>
        </ModalActions>
      </form>
    </Modal>
  );
};
